import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, LayoutDashboard, LogOut } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";

const navLinks = [
  { id: "buyers", label: "For Buyers" },
  { id: "businesses", label: "For Businesses" },
  { id: "categories", label: "Categories" },
  { id: "how-it-works", label: "How It Works" },
  { id: "trust", label: "About Yuukke" },
];

export default function Header({ onNav }) {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setMobileOpen(false);
    onNav?.(id);
  };

  const goSignup = () => { setMobileOpen(false); nav("/signup"); };
  const goLogin = () => { setMobileOpen(false); nav("/login"); };
  const goDashboard = () => { setMenuOpen(false); nav(user?.role === "admin" ? "/admin" : "/dashboard"); };

  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-brand-paper/90 backdrop-blur-xl border-b border-brand-line"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="brand-logo" aria-label="Yuukke home">
          <img src="/yuukke-logo.png" alt="Yuukke" className="h-8 md:h-9 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
          {navLinks.map((l) => (
            <button
              key={l.id}
              onClick={() => handleNav(l.id)}
              className="link-underline text-[15px] font-medium text-brand-muted hover:text-brand-wine transition-colors"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <div className="relative">
              <button
                onClick={() => setMenuOpen((s) => !s)}
                className="flex items-center gap-2 pl-2 pr-3 py-1.5 bg-brand-cream border border-brand-line rounded-full hover:border-brand-wine/40 transition-all"
                data-testid="header-user-chip"
              >
                <span className="w-8 h-8 rounded-full bg-brand-wine text-white font-semibold flex items-center justify-center text-sm">
                  {initial}
                </span>
                <span className="text-sm font-medium text-brand-ink max-w-[140px] truncate">
                  {user.name || user.email}
                </span>
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-brand-cream border border-brand-line rounded-xl shadow-lift-sm overflow-hidden" data-testid="header-user-menu">
                  <button onClick={goDashboard} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-brand-ink hover:bg-brand-paper2 transition-colors" data-testid="header-dashboard-link">
                    <LayoutDashboard size={14} /> Dashboard
                  </button>
                  <button onClick={async () => { setMenuOpen(false); await logout(); nav("/"); }} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-brand-wine hover:bg-brand-paper2 transition-colors" data-testid="header-logout">
                    <LogOut size={14} /> Log out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <button onClick={goLogin} className="text-[15px] font-medium text-brand-wine border border-brand-wine/40 rounded-lg px-5 py-2.5 hover:bg-brand-wine hover:text-white transition-all" data-testid="header-login">
                Login
              </button>
              <button onClick={goSignup} className="text-[15px] font-medium bg-brand-wine text-white rounded-lg px-6 py-2.5 hover:bg-brand-wineDark hover:-translate-y-0.5 transition-all shadow-[0_8px_18px_-10px_rgba(136,34,54,0.55)]" data-testid="header-join-cta">
                Join the Business Exchange
              </button>
            </>
          )}
        </div>

        <button
          className="lg:hidden p-2 text-brand-ink"
          onClick={() => setMobileOpen((s) => !s)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden bg-brand-paper border-t border-brand-line px-6 py-6 space-y-3"
          data-testid="mobile-menu"
        >
          {navLinks.map((l) => (
            <button key={l.id} onClick={() => handleNav(l.id)} className="block text-brand-ink text-lg py-2 font-medium" data-testid={`mobile-nav-${l.id}`}>
              {l.label}
            </button>
          ))}
          <div className="pt-2 flex gap-3">
            {user ? (
              <>
                <button onClick={goDashboard} className="flex-1 bg-brand-wine text-white rounded-lg py-2.5 font-medium" data-testid="mobile-dashboard">
                  Dashboard
                </button>
                <button onClick={async () => { setMobileOpen(false); await logout(); nav("/"); }} className="flex-1 text-brand-wine border border-brand-wine rounded-lg py-2.5 font-medium" data-testid="mobile-logout">
                  Log out
                </button>
              </>
            ) : (
              <>
                <button onClick={goLogin} className="flex-1 text-brand-wine border border-brand-wine rounded-lg py-2.5 font-medium" data-testid="mobile-login">
                  Login
                </button>
                <button onClick={goSignup} className="flex-1 bg-brand-wine text-white rounded-lg py-2.5 font-medium" data-testid="mobile-join-cta">
                  Join
                </button>
              </>
            )}
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
